import { fmtDateTime } from '@/lib/dates.js';
import { useState, useMemo, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Building2, Users as UsersIcon, X, Plus, Trash2, Edit2, Check,
  Mail, RotateCcw, Send,
} from 'lucide-react';
import { adminSites, adminUsers, team, type AdminUserRow } from '@/api/client';
import { humanRole, humanStatus } from '@/lib/humanise';
import { SITE_CHANGE_ROLES, initialSiteRole } from '@/lib/siteRoles';
import { PageHeader } from '@/components/layout/PageHeader';
import { listTimeZones } from '@/lib/timezones';
import { SingleSelectSearch } from '@/components/ui/SingleSelectSearch';
import { toast } from 'sonner';
import { errorText } from '@/lib/permissionError';


/**
 * Site Detail — shows site metadata + Members tab.
 *
 * Members tab uses the direct-assign endpoints to add/remove existing users
 * from this site without the invite-email round-trip. Sprint-1 spec: a user
 * who's already in the system can be added to a second/third site here.
 */

const ROLES = [
  'site_admin', 'principal_investigator', 'sub_investigator', 'coordinator',
  'site_study_designer', 'regulatory_specialist', 'site_pharma',
  'finance_billing', 'monitor', 'auditor',
];

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [adding, setAdding] = useState<{ user_id: string; role: string } | null>(null);
  const [editingMember, setEditingMember] = useState<{ user: AdminUserRow; role: string } | null>(null);
  const [inviting, setInviting] = useState<{ email: string; full_name: string } | null>(null);
  const [tzDraft, setTzDraft] = useState<string | null>(null);

  const sites = useQuery({ queryKey: ['admin-sites'], queryFn: () => adminSites.list() });
  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => adminUsers.list() });
  const pendingInvites = useQuery({
    queryKey: ['team-invitations', id],
    queryFn: () => team.listPending(id!),
    enabled: !!id,
  });

  const site = useMemo(
    () => sites.data?.sites.find((s) => s.site_id === id),
    [sites.data, id],
  );

  const members = useMemo(() => {
    if (!users.data || !id) return [];
    return users.data.rows
      .map((u) => {
        const m = u.sites.find((s) => s.site_id === id);
        return m ? { user: u, role: m.role } : null;
      })
      .filter((x): x is { user: AdminUserRow; role: string | null } => x !== null);
  }, [users.data, id]);

  const nonMembers = useMemo(() => {
    if (!users.data || !id) return [];
    return users.data.rows.filter((u) => !u.sites.some((s) => s.site_id === id));
  }, [users.data, id]);

  const onError = (e: unknown) =>
    toast.error(errorText(e, 'Action failed'));

  const tzMut = useMutation({
    mutationFn: (tz: string) => adminSites.update(id!, { time_zone: tz }),
    onSuccess: () => {
      toast.success('Time zone updated');
      qc.invalidateQueries({ queryKey: ['admin-sites'] });
    },
    onError: (e) => toast.error(errorText(e, 'Failed to update time zone')),
  });

  const addMut = useMutation({
    mutationFn: () => adminUsers.addSite(adding!.user_id, { site_id: id!, role: adding!.role }),
    onSuccess: () => {
      toast.success('User assigned to site');
      setAdding(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError,
  });

  const removeMut = useMutation({
    mutationFn: (userId: string) => adminUsers.removeSite(userId, id!),
    onSuccess: () => {
      toast.success('User removed from site');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError,
  });

  const editRoleMut = useMutation({
    mutationFn: () =>
      adminUsers.changeRole(editingMember!.user.user_id, id!, editingMember!.role),
    onSuccess: () => {
      toast.success('Role updated');
      setEditingMember(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError,
  });

  const inviteMut = useMutation({
    mutationFn: () =>
      team.invite(id!, {
        email: inviting!.email.trim(),
        full_name: inviting!.full_name.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(`Invitation sent to ${inviting?.email.trim()}`);
      setInviting(null);
      qc.invalidateQueries({ queryKey: ['team-invitations', id] });
    },
    onError,
  });

  const resendMut = useMutation({
    mutationFn: (invite_id: string) => team.resend(id!, invite_id),
    onSuccess: () => {
      toast.success('Invitation resent');
      qc.invalidateQueries({ queryKey: ['team-invitations', id] });
    },
    onError,
  });

  function handleAddSubmit(e: FormEvent) {
    e.preventDefault();
    if (!adding || !adding.user_id) return;
    addMut.mutate();
  }

  function handleEditSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editingMember) return;
    editRoleMut.mutate();
  }

  function handleInviteSubmit(e: FormEvent) {
    e.preventDefault();
    if (!inviting || !inviting.email.trim()) return;
    inviteMut.mutate();
  }

  if (sites.isLoading || users.isLoading) {
    return (
      <div className="card flex items-center justify-center gap-2 p-8 text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (!site) {
    return (
      <div className="card p-8 text-center text-sm text-ink-muted">
        <Building2 className="mx-auto mb-2 h-10 w-10 opacity-50" />
        Site not found. <Link to="/admin/sites" className="text-blue underline">Back to Sites</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin/sites"
        title={site.name}
        subtitle={
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-muted">
            {site.reference_number && <span className="font-mono">{site.reference_number}</span>}
            <span className={site.status === 'active' ? 'badge badge-success' : 'badge badge-warn'}>
              {humanStatus(site.status)}
            </span>
          </div>
        }
      />

      <div className="card p-4">
        <label className="label text-xs" htmlFor="site_tz">Time zone</label>
        <div className="mt-1 flex items-center gap-2">
          <div className="w-full max-w-xs">
            <SingleSelectSearch
              options={listTimeZones().map((tz) => ({ value: tz, label: tz }))}
              value={tzDraft ?? site.time_zone ?? null}
              onChange={(next) => setTzDraft(next ?? '')}
              placeholder="Search time zones…"
              disabled={tzMut.isPending}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={tzMut.isPending || !tzDraft || tzDraft === site.time_zone}
            onClick={() => tzDraft && tzMut.mutate(tzDraft)}
          >
            {tzMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="mt-1 text-xs text-ink-muted">
          Timestamps for this site display in this zone. Data is stored in UTC.
        </p>
      </div>

      {/* Pending invitations — invite NEW users to this site (existing flow,
          the team.invite API). Distinct from "Add existing user" below which
          assigns an already-onboarded user without email. */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-surface-border bg-surface-muted px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
            <Mail className="h-4 w-4" />
            Pending invitations ({pendingInvites.data?.rows.length ?? 0})
          </div>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => setInviting({ email: '', full_name: '' })}
          >
            <Send className="h-3.5 w-3.5" /> Invite new user
          </button>
        </div>
        {pendingInvites.isLoading ? (
          <div className="flex items-center justify-center gap-2 p-4 text-xs text-ink-muted">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading invitations…
          </div>
        ) : !pendingInvites.data || pendingInvites.data.rows.length === 0 ? (
          <div className="p-6 text-center text-xs text-ink-muted">
            No pending invitations. Click <strong>Invite new user</strong> to send a setup link
            for someone who doesn't yet have an account.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role on accept</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingInvites.data.rows.map((inv) => (
                <tr key={inv.invite_id} className="border-b border-surface-border last:border-0">
                  <td className="px-3 py-2">{inv.email}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="badge badge-info">{humanRole(inv.role)}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-muted">
                    {fmtDateTime(inv.expires_at_iso)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() => resendMut.mutate(inv.invite_id)}
                      disabled={resendMut.isPending}
                    >
                      <RotateCcw className="h-3 w-3" /> Resend
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-surface-border bg-surface-muted px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
            <UsersIcon className="h-4 w-4" />
            Members ({members.length})
          </div>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => setAdding({ user_id: '', role: 'monitor' })}
            disabled={nonMembers.length === 0}
            title={nonMembers.length === 0 ? 'Every existing user is already a member of this site' : undefined}
          >
            <Plus className="h-3.5 w-3.5" /> Add existing user
          </button>
        </div>

        {members.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-muted">
            <UsersIcon className="mx-auto mb-2 h-10 w-10 opacity-50" />
            No members yet. Click <strong>Add existing user</strong> to assign someone, or use the Invite
            button on the topbar to invite a brand-new user.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map(({ user, role }) => (
                <tr key={user.user_id} className="border-b border-surface-border last:border-0">
                  <td className="px-3 py-2">
                    <div className="font-medium">{user.full_name || user.email.split('@')[0]}</div>
                    <div className="text-xs text-ink-muted">{user.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="badge badge-info text-xs">{role ? humanRole(role) : 'No role'}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {user.locked_at ? (
                      <span className="badge badge-danger">Locked</span>
                    ) : (
                      <span className={user.status === 'active' ? 'badge badge-success' : 'badge badge-warn'}>
                        {humanStatus(user.status)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() => setEditingMember({ user, role: initialSiteRole(role, SITE_CHANGE_ROLES) })}
                    >
                      <Edit2 className="h-3.5 w-3.5" /> Change role
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-xs text-danger-700 ml-1"
                      onClick={() => {
                        if (window.confirm(`Remove ${user.email} from ${site.name}?\nTheir membership and per-site role will be revoked.`)) {
                          removeMut.mutate(user.user_id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add existing user modal */}
      {adding && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={handleAddSubmit} className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">Add existing user to {site.name}</h2>
              <button type="button" onClick={() => setAdding(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              Pick an existing user. No invitation email is sent — they'll see the new site in their
              Site Switcher on next login.
            </p>
            <div>
              <label className="label text-xs">User</label>
              <select
                className="input" required
                value={adding.user_id}
                onChange={(e) => setAdding({ ...adding, user_id: e.target.value })}
              >
                <option value="" disabled>Pick a user…</option>
                {nonMembers.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.full_name ? `${u.full_name} (${u.email})` : u.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-xs">Role at this site</label>
              <select className="input" value={adding.role}
                onChange={(e) => setAdding({ ...adding, role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{humanRole(r)}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setAdding(null)}>Cancel</button>
              <button type="submit" className="btn-primary"
                disabled={addMut.isPending || !adding.user_id}>
                {addMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Assign
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Invite NEW user modal */}
      {inviting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={handleInviteSubmit} className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">Invite new user to {site.name}</h2>
              <button type="button" onClick={() => setInviting(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              Sends a setup-link email. The invitee joins as <strong>Site Admin</strong>;
              per-study roles are assigned later from Study Onboarding. Link expires in 48 hours.
            </p>
            <div>
              <label className="label text-xs">Work email *</label>
              <input
                type="email" required
                className="input"
                placeholder="colleague@organisation.com"
                value={inviting.email}
                onChange={(e) => setInviting({ ...inviting, email: e.target.value })}
              />
            </div>
            <div>
              <label className="label text-xs">Full name (optional)</label>
              <input
                type="text"
                className="input"
                placeholder="Jane Smith"
                value={inviting.full_name}
                onChange={(e) => setInviting({ ...inviting, full_name: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setInviting(null)}>Cancel</button>
              <button type="submit" className="btn-primary"
                disabled={inviteMut.isPending || !inviting.email.trim()}>
                {inviteMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send invite
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Change role modal */}
      {editingMember && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={handleEditSubmit} className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">Change role</h2>
              <button type="button" onClick={() => setEditingMember(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              Changing role for <strong className="text-ink-primary">{editingMember.user.email}</strong> at{' '}
              <strong className="text-ink-primary">{site.name}</strong>.
            </p>
            <div>
              <label className="label text-xs">New role</label>
              <select className="input" value={editingMember.role}
                onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value })}>
                {/* Change role: invitable roles plus Monitor (shared with the BE). */}
                {SITE_CHANGE_ROLES.map((r) => <option key={r} value={r}>{humanRole(r)}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setEditingMember(null)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={editRoleMut.isPending}>
                {editRoleMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
